import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  EyeIcon,
  ArrowLeftIcon,
  TrophyIcon,
  PuzzlePieceIcon,
} from '@heroicons/react/24/outline';
import Card from '../components/UI/Card';
import userService, {
  UserGamesMode,
} from '../api/services/user.service';
import GameHistoryDetailModal from '../components/gameHIstory/GameHIstoryDetailModal';

/* =========================================================
   HELPERS
========================================================= */
const formatDuration = (ms: number): string => {
  if (!ms || ms <= 0) return '0s';
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min > 0) return `${min}m ${sec}s`;
  return `${sec}s`;
};

const UserGames: React.FC = () => {
  const navigate = useNavigate();
  const { id: userId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // mode comes from the URL so it's shareable / survives refresh
  // useMemo keeps the value byte-stable across renders so the
  // queryKey never changes identity unless the URL actually does.
  const mode: UserGamesMode = useMemo(
    () => (searchParams.get('mode') === 'won' ? 'won' : 'played'),
    [searchParams]
  );

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearch, setLocalSearch] = useState('');
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');
  const [detailGameId, setDetailGameId] = useState<string | null>(null);

  /* =========================================================
     USER GAMES QUERY
     - Same caching strategy as GameHistory.tsx / Dashboard.tsx
     - 5m staleTime → cached list renders instantly
     - refetchOnMount: false → no re-fetch on remount
     - refetchOnWindowFocus: false → no re-fetch on tab switch
     - refetchOnReconnect: false → no re-fetch on network blip
     - mode is part of the key so played/won cache separately
  ========================================================= */
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'user-games',
      userId,
      mode,
      page,
      limit,
      searchTerm,
      minDate,
      maxDate,
    ],
    queryFn: () =>
      userService.getUserGames(userId as string, {
        page,
        limit,
        search: searchTerm || undefined,
        minDate: minDate || undefined,
        maxDate: maxDate || undefined,
        mode,
      }),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  const games = data?.games || [];
  const pagination = data?.pagination || {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  };

  const handleSearchSubmit = () => {
    setSearchTerm(localSearch);
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearchSubmit();
  };

  const handleClearFilters = () => {
    setLocalSearch('');
    setSearchTerm('');
    setMinDate('');
    setMaxDate('');
    setPage(1);
  };

  const handleModeChange = (next: UserGamesMode) => {
    if (next === mode) return;
    setSearchParams({ mode: next });
    setPage(1);
  };

  const hasFilters = !!searchTerm || !!minDate || !!maxDate;

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <button
            onClick={() => navigate('/users')}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-2"
          >
            <ArrowLeftIcon className="w-3.5 h-3.5" />
            Back to Users
          </button>
          <h1 className="text-2xl font-bold text-gray-900">User Games</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {mode === 'won'
              ? 'Games this user won.'
              : 'Games this user played.'}
          </p>
        </div>

        {/* Mode toggle */}
        <div className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
          <button
            onClick={() => handleModeChange('played')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              mode === 'played'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <PuzzlePieceIcon className="w-3.5 h-3.5" />
            Played
          </button>
          <button
            onClick={() => handleModeChange('won')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              mode === 'won'
                ? 'bg-white text-green-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <TrophyIcon className="w-3.5 h-3.5" />
            Won
          </button>
        </div>
      </div>

      {/* Filters */}
      <Card padding="sm" className="mb-4">
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by history id or room id..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-full text-sm border border-gray-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 whitespace-nowrap">
              From
            </label>
            <input
              type="date"
              value={minDate}
              onChange={(e) => {
                setMinDate(e.target.value);
                setPage(1);
              }}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 whitespace-nowrap">
              To
            </label>
            <input
              type="date"
              value={maxDate}
              onChange={(e) => {
                setMaxDate(e.target.value);
                setPage(1);
              }}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <button
            onClick={handleSearchSubmit}
            className="text-sm px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            Search
          </button>

          {hasFilters && (
            <button
              onClick={handleClearFilters}
              className="text-sm px-4 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card padding="sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary-500 border-t-transparent" />
          </div>
        ) : error ? (
          <div className="text-center py-6">
            <p className="text-sm text-red-500">
              {error instanceof Error
                ? error.message
                : 'Failed to load games'}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-3 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600"
            >
              Retry
            </button>
          </div>
        ) : games.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-sm text-gray-500">
              {mode === 'won'
                ? 'This user has not won any games yet'
                : 'This user has not played any games yet'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-[10px] text-gray-500 uppercase tracking-wide border-b border-gray-100">
                  <th className="px-3 py-2 font-medium">History ID</th>
                  <th className="px-3 py-2 font-medium">Room</th>
                  <th className="px-3 py-2 font-medium">Bet</th>
                  <th className="px-3 py-2 font-medium">Players</th>
                  <th className="px-3 py-2 font-medium">Prize Pool</th>
                  <th className="px-3 py-2 font-medium">Winners</th>
                  <th className="px-3 py-2 font-medium">Duration</th>
                  <th className="px-3 py-2 font-medium">Finished</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {games.map((g) => (
                  <tr
                    key={g.id}
                    className="border-b border-gray-50 hover:bg-gray-50/60"
                  >
                    <td className="px-3 py-3">
                      <p className="text-xs font-medium text-gray-900 truncate max-w-[140px]">
                        {g.historyId}
                      </p>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600">
                      {g.roomId}
                    </td>
                    <td className="px-3 py-3 text-xs font-medium text-gray-900">
                      ETB {g.betAmount.toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600">
                      {g.playersCount}
                      <span className="text-[10px] text-gray-400 ml-1">
                        ({g.totalCartelasSold} cartelas)
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs font-medium text-green-600">
                      ETB {g.prizePool.toLocaleString()}
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          g.winnersCount > 0
                            ? 'bg-green-50 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            g.winnersCount > 0 ? 'bg-green-500' : 'bg-gray-400'
                          }`}
                        />
                        {g.winnersCount} winner
                        {g.winnersCount !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600">
                      {formatDuration(g.durationMs)}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600 whitespace-nowrap">
                      {new Date(g.finishedAt).toLocaleString()}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setDetailGameId(g.id)}
                          className="p-1.5 rounded-lg hover:bg-primary-50 text-primary-600 transition-colors"
                          title="View"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="flex items-center justify-between px-3 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Page {pagination.page} of {pagination.pages} ·{' '}
              {pagination.total} total
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-2.5 py-1 text-xs rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                Prev
              </button>
              <button
                onClick={() =>
                  setPage((p) => Math.min(pagination.pages, p + 1))
                }
                disabled={page >= pagination.pages}
                className="px-2.5 py-1 text-xs rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Detail modal — the exact same one as Game History */}
      <GameHistoryDetailModal
        isOpen={!!detailGameId}
        gameId={detailGameId}
        onClose={() => setDetailGameId(null)}
      />
    </div>
  );
};

export default UserGames;